// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — one deal, played by whoever is here: a bot, a peer, or a bot standing in for a peer.
// Solo and multiplayer are the SAME object with different flags, because the alternative is two code
// paths that disagree about the rules.
//
// ── WHY HOST-AUTHORITATIVE, AND WHY A GUEST NEVER APPLIES OPTIMISTICALLY ─────────────────────────
//
// Two things are hidden: the satchels, and the crate order. A guest that applied its own action
// locally would be branching on state it does not have — the refill after a TAKE draws a card only
// the host can name — and from that ply on the two clients are playing different games. So a guest
// PROPOSES and the host DECIDES. That costs one round trip on a turn-based game and cannot desync.
//
// ── WHAT TRAVELS ────────────────────────────────────────────────────────────────────────────────
//
// A whole snapshot, every time, unicast per recipient: the full public table plus THAT seat's own
// satchel and nothing else. Never a delta — so a dropped, duplicated or reordered message is a
// non-event, and a peer that connects late is caught up by the next heartbeat rather than by a
// special case.
//
// ── AND WHY A HOST LEAVING IS A WALKOVER, NOT A TAKEOVER ────────────────────────────────────────
//
// A promoted peer cannot rebuild the crate. Nobody has ever seen it, so unlike a game with no draw
// pile there is no way to re-attest it from what the other players can see, and a freshly shuffled
// substitute would silently change which wares are still coming — the single most important fact in
// the game. So the honest ending is: stop here, keep both scores exactly as they stand, award the
// deal to the player who is still in the room, and show the full summary. It is announced with a
// visible reason and a horizon, never a frozen board, and it is armed from BOTH `onPeerLeave` and
// twenty seconds of heartbeat silence — because Trystero does not reliably report a tab that simply
// vanished.

import { makeRng } from '@ben-gy/game-engine/rng';
import { WARES, type Ware } from './wares';
import { modeOf, type Mode } from './modes';
import {
  beginTurn,
  clonePublic,
  end,
  legalActions,
  other,
  step,
  type Action,
  type FullState,
  type PublicEvent,
  type PublicState,
  type Seat,
} from './rules';
import { viewFor } from './secret';
import { chooseAction, type Skill } from './bot';
import { openDeal } from './game';

/** How often the host re-sends everything, so a peer that missed a message catches up. */
export const BEAT_MS = 2000;
/** No word at all from the other seat for this long and the deal is closed as a walkover. */
export const SILENT_MS = 20000;
/** How long the survivor is shown a reason and a countdown before the walkover lands. */
export const WALKOVER_MS = 9000;

export type Wire =
  /** Host -> ONE peer. The only private message, and the only one that differs per recipient. */
  | {
      t: 'sync';
      r: number;
      seat: number;
      pub: WirePub;
      satchel: Ware[];
      pennants: [number, number];
      /** Sent once the deal is over: both satchels, so the summary can show what was stranded. */
      reveal?: [Ware[], Ware[]];
    }
  /** Peer -> host. A proposal. The host is the only thing that decides. */
  | { t: 'act'; r: number; a: Action }
  /** Peer -> host. "I have nothing — send it all again." */
  | { t: 'need'; r: number }
  /** Anyone -> everyone. "Still here." Silence, not a callback, is what arms the walkover. */
  | { t: 'beat'; r: number };

/** The public table on the wire. The mode travels as an id and is re-validated on arrival. */
export interface WirePub extends Omit<PublicState, 'mode'> {
  mode: string;
}

export const encodePub = (p: PublicState): WirePub => ({ ...clonePublic(p), mode: p.mode.id });

export function decodePub(w: WirePub): PublicState {
  // `modeOf` guards the id with Object.hasOwn: an unknown id off the wire falls back to the default
  // rather than reaching the renderer as a Mode of undefined fields.
  const mode: Mode = modeOf(w.mode);
  const p = { ...w, mode } as unknown as PublicState;
  return clonePublic(p);
}

export interface SessionDeps {
  send: (m: Wire, to?: string) => void;
  onChange: () => void;
  onEvents: (evs: PublicEvent[]) => void;
  onEnd: () => void;
  now?: () => number;
}

export interface SessionInit {
  mode: Mode;
  round: number;
  mySeat: Seat | null;
  /** Seats the local client plays for itself. Solo has one; a room has one; a spectator has none. */
  botSeat: Seat | null;
  skill: Skill;
  isHost: boolean;
  names: string[];
  /** Peer id per seat. Empty strings in solo. */
  peerOfSeat: string[];
  seed: number;
  starter: Seat;
  pennants: [number, number];
  deps: SessionDeps;
  botDelayMs?: number;
  /** Tests and the sim pass one; the shipped game never does. */
  shuffleSeed?: number;
}

export class Session {
  readonly mode: Mode;
  readonly round: number;
  readonly seed: number;
  mySeat: Seat | null;
  botSeat: Seat | null;
  isHost: boolean;
  names: string[];
  peerOfSeat: string[];
  pennants: [number, number];
  deps: SessionDeps;

  /** Host only. THE SECRET — never sent, never logged, never rendered. */
  private full: FullState | null = null;
  /** Everyone. The table as this client is allowed to hold it. */
  pub: PublicState | null = null;
  satchel: Ware[] = [];
  reveal: [Ware[], Ware[]] | null = null;

  /** Set when the room has collapsed to one player and the deal is being closed. */
  walkoverAt = 0;
  note = '';

  private readonly skill: Skill;
  private readonly botDelayMs: number;
  private botTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEvents = 0;
  private heardAt = new Map<string, number>();
  private startedAt = 0;
  private ended = false;
  private gone = new Set<string>();

  constructor(init: SessionInit) {
    this.mode = init.mode;
    this.round = init.round;
    this.seed = init.seed;
    this.mySeat = init.mySeat;
    this.botSeat = init.botSeat;
    this.isHost = init.isHost;
    this.names = init.names;
    this.peerOfSeat = init.peerOfSeat;
    this.pennants = init.pennants;
    this.deps = init.deps;
    this.skill = init.skill;
    this.botDelayMs = init.botDelayMs ?? 650;
    this.startedAt = this.now();

    if (this.isHost) {
      this.full = openDeal({
        mode: this.mode,
        dealNo: init.round,
        starter: init.starter,
        shuffleSeed: init.shuffleSeed,
      });
      this.adoptHost();
      this.push();
      this.changed();
      this.maybeBot();
    } else {
      this.deps.send({ t: 'need', r: this.round });
    }
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  private adoptHost(): void {
    if (!this.full) return;
    this.pub = this.full.pub;
    if (this.mySeat !== null) this.satchel = this.full.sec.satchels[this.mySeat];
  }

  // ── the wire ──────────────────────────────────────────────────────────────────────────────────

  private push(to?: number): void {
    const fs = this.full;
    if (!fs || !this.isHost) return;
    const over = fs.pub.over;
    const reveal: [Ware[], Ware[]] | undefined = over
      ? [[...fs.sec.satchels[0]], [...fs.sec.satchels[1]]]
      : undefined;
    for (let seat = 0; seat < 2; seat++) {
      if (to !== undefined && seat !== to) continue;
      const peer = this.peerOfSeat[seat];
      if (!peer || seat === this.mySeat) continue;
      this.deps.send(
        {
          t: 'sync',
          r: this.round,
          seat,
          pub: encodePub(fs.pub),
          satchel: [...fs.sec.satchels[seat]],
          pennants: [...this.pennants] as [number, number],
          ...(reveal ? { reveal } : {}),
        },
        peer,
      );
    }
    if (over) this.reveal = [[...fs.sec.satchels[0]], [...fs.sec.satchels[1]]];
  }

  receive(msg: Wire, from: string): void {
    if (msg.r !== this.round) return;
    this.heardAt.set(from, this.now());
    this.gone.delete(from);

    if (msg.t === 'sync') {
      if (this.isHost) return;
      const next = decodePub(msg.pub);
      // A late or duplicated snapshot must never roll the table backwards.
      if (this.pub && next.ply < this.pub.ply && !next.over) return;
      this.pub = next;
      this.mySeat = msg.seat as Seat;
      this.satchel = [...msg.satchel];
      this.pennants = msg.pennants;
      if (msg.reveal) this.reveal = [[...msg.reveal[0]], [...msg.reveal[1]]];
      this.changed();
      return;
    }

    if (msg.t === 'act') {
      if (!this.isHost || !this.full) return;
      // A peer may only act for ITS OWN seat. Without this one compare, either player could take the
      // other's turn — which in a duel is not a cheat so much as a different, much worse game.
      const seat = this.peerOfSeat.indexOf(from) as Seat;
      if (seat < 0 || this.full.pub.toMove !== seat) return;
      this.commit(msg.a);
      return;
    }

    if (msg.t === 'need') {
      if (!this.isHost) return;
      const seat = this.peerOfSeat.indexOf(from);
      this.push(seat >= 0 ? seat : undefined);
    }
  }

  // ── actions ───────────────────────────────────────────────────────────────────────────────────

  submit(a: Action): boolean {
    if (!this.pub || this.pub.over) return false;
    if (this.mySeat === null || this.pub.toMove !== this.mySeat) return false;
    if (this.isHost) return this.commit(a);
    this.deps.send({ t: 'act', r: this.round, a });
    return true;
  }

  private commit(a: Action): boolean {
    const fs = this.full;
    if (!fs || !this.isHost) return false;
    if (!step(fs, a)) return false;
    this.adoptHost();
    this.push();
    this.changed();
    this.maybeBot();
    return true;
  }

  /** Whether the local client plays this seat itself. Only ever the host's own bot. */
  private drives(seat: Seat): boolean {
    return this.isHost && this.botSeat === seat;
  }

  private maybeBot(): void {
    const fs = this.full;
    if (!fs || this.botTimer !== null || fs.pub.over) return;
    if (!this.drives(fs.pub.toMove)) return;
    // setTimeout, never rAF: a backgrounded tab never fires rAF, and an opponent that stops thinking
    // the moment the player checks a message is a hang, not a pause.
    this.botTimer = setTimeout(() => {
      this.botTimer = null;
      const cur = this.full;
      if (!cur || cur.pub.over || !this.drives(cur.pub.toMove)) return;
      const seat = cur.pub.toMove;
      const rng = makeRng(`${this.seed}:${cur.pub.ply}:${seat}:${this.skill.id}`);
      const choice = chooseAction(viewFor(cur, seat), this.skill, rng);
      if (!legalActions(cur.pub, seat, cur.sec.satchels[seat]).length) return;
      this.commit(choice.action);
    }, this.botDelayMs);
  }

  // ── the room changing shape ───────────────────────────────────────────────────────────────────

  /**
   * The other player has gone. Arm the walkover with a visible horizon rather than closing the deal
   * out from under a player who is looking at the board — and never freeze.
   */
  peerLeft(peerId: string): void {
    const seat = this.peerOfSeat.indexOf(peerId);
    if (seat < 0) return;
    this.gone.add(peerId);
    this.peerOfSeat[seat] = '';
    this.armWalkover();
  }

  private armWalkover(): void {
    if (!this.pub || this.pub.over || this.walkoverAt > 0) return;
    if (this.mySeat === null) return;
    this.walkoverAt = this.now() + WALKOVER_MS;
    this.changed();
  }

  /** Ms until the walkover lands, or null. Rendered — a silent wait is a hang. */
  walkoverIn(): number | null {
    if (this.walkoverAt === 0) return null;
    return Math.max(0, this.walkoverAt - this.now());
  }

  /**
   * Called on a timer by the screen. Two jobs that must not be collapsed: land an armed walkover,
   * and notice a peer that vanished without Trystero ever saying so.
   */
  tick(): void {
    if (!this.pub || this.pub.over) return;
    const t = this.now();
    if (this.mySeat !== null) {
      for (let seat = 0; seat < 2; seat++) {
        if (seat === this.mySeat) continue;
        const peer = this.peerOfSeat[seat];
        if (!peer) continue;
        const last = this.heardAt.get(peer) ?? this.startedAt;
        if (t - last > SILENT_MS) this.armWalkover();
      }
    }
    if (this.walkoverAt > 0 && t >= this.walkoverAt) this.finishWalkover();
  }

  private finishWalkover(): void {
    if (!this.pub || this.pub.over) return;
    if (this.isHost && this.full) {
      end(this.full, 'walkover');
      this.adoptHost();
      this.push();
    } else {
      // A guest that lost the host closes its own copy. Nothing further arrives, so nothing can
      // contradict it, and the player reaches a real summary instead of a frozen board.
      end({ pub: this.pub, sec: { satchels: [[], []], crate: [] } }, 'walkover');
    }
    this.walkoverAt = 0;
    this.changed();
  }

  /** Whoever is still in the room takes a walkover deal. */
  winner(): Seat {
    const p = this.pub;
    if (!p) return 0;
    if (p.endReason === 'walkover' && this.mySeat !== null) return this.mySeat;
    const a = scoreTotal(p, 0);
    const b = scoreTotal(p, 1);
    if (a !== b) return a > b ? 0 : 1;
    if (p.gang[0] !== p.gang[1]) return p.gang[0] > p.gang[1] ? 0 : 1;
    return other(p.starter);
  }

  /** Both satchels for the summary. A guest that never got the reveal shows counts it does know. */
  satchelsForSummary(): [Ware[], Ware[]] {
    if (this.reveal) return this.reveal;
    const out: [Ware[], Ware[]] = [[], []];
    if (this.mySeat !== null) out[this.mySeat] = [...this.satchel];
    const p = this.pub;
    if (p) {
      const them = this.mySeat === null ? 1 : other(this.mySeat);
      out[them] = new Array(p.satchelCount[them]).fill(WARES[0]) as Ware[];
    }
    return out;
  }

  beat(): void {
    if (this.isHost) this.push();
    else this.deps.send({ t: 'beat', r: this.round });
  }

  /** Only meaningful for the host: keeps a stalled turn moving in solo. */
  nudge(): void {
    if (this.full && !this.full.pub.over) beginTurn(this.full);
    this.maybeBot();
  }

  private changed(): void {
    const p = this.pub;
    if (p && p.log.length > this.lastEvents) {
      this.deps.onEvents(p.log.slice(this.lastEvents));
      this.lastEvents = p.log.length;
    }
    this.deps.onChange();
    if (p && p.over && !this.ended) {
      this.ended = true;
      this.deps.onEnd();
    }
  }

  destroy(): void {
    if (this.botTimer !== null) clearTimeout(this.botTimer);
    this.botTimer = null;
  }
}

function scoreTotal(p: PublicState, seat: Seat): number {
  const hire = p.gang[seat] > p.gang[other(seat)] ? 6 : 0;
  const tare = seat === p.starter ? 0 : 2;
  return p.chits[seat] + p.bounty[seat] + hire + tare;
}
